import moment from 'moment-timezone';
import { formatClock } from '../../helpers/telemedCall';

export const VIEWPORT_MARGIN = 8;

// rect: getBoundingClientRect() do painel com o translate atual já aplicado.
// Devolve os limites de translate no formato do `bounds` do react-draggable.
export function viewportBounds({
  rect,
  position,
  viewport,
  margin = VIEWPORT_MARGIN,
}) {
  const naturalLeft = rect.left - position.x;
  const naturalTop = rect.top - position.y;
  return {
    left: margin - naturalLeft,
    top: margin - naturalTop,
    right: viewport.width - margin - rect.width - naturalLeft,
    bottom: viewport.height - margin - rect.height - naturalTop,
  };
}

// Painel maior que a viewport prende no início do eixo: a barra de título fica alcançável.
function clampAxis(value, min, max) {
  if (max < min) {
    return min;
  }
  return Math.min(Math.max(value, min), max);
}

export function clampPosition({
  rect,
  position,
  viewport,
  margin,
}) {
  const bounds = viewportBounds({
    rect,
    position,
    viewport,
    margin,
  });
  return {
    x: clampAxis(position.x, bounds.left, bounds.right),
    y: clampAxis(position.y, bounds.top, bounds.bottom),
  };
}

// Estado da chamada do lado do profissional: um só ponto de verdade para o selo do
// título, o aviso sobre o vídeo e o widget minimizado.
// tone: live | waiting | warning | error · note: aviso leve no topo do palco ·
// overlay: cobre o palco ({ title, text, action: 'retry' | 'none' }).
export function callStatus(flags = {}) {
  const ending = flags.endingTelemedRoom === true
    || Boolean(flags.otherPeerEndedCall && flags.hasEverConnected);
  if (ending) {
    return {
      tone: 'warning',
      label: 'encerrando…',
      overlay: { title: 'Encerrando a chamada…', action: 'none' },
    };
  }
  if (flags.connectionTimedOut) {
    return {
      tone: 'error',
      label: 'não foi possível conectar',
      overlay: {
        title: 'Não foi possível conectar',
        text: 'O paciente não entrou na sala a tempo. Tente de novo ou avise pelo WhatsApp.',
        action: 'retry',
      },
    };
  }
  if (flags.fullyConnected) {
    return { tone: 'live', label: `ao vivo · ${formatClock(flags.elapsedSeconds)}` };
  }
  if (flags.noMediaDevices) {
    return { tone: 'error', label: 'sem câmera e microfone' };
  }
  if (flags.userMediaError) {
    return { tone: 'error', label: 'câmera e microfone bloqueados' };
  }
  if (!flags.userMediaAvailable) {
    return { tone: 'waiting', label: 'preparando câmera…' };
  }
  if (flags.hasEverConnected) {
    return {
      tone: 'warning',
      label: 'reconectando…',
      overlay: { title: 'Reconectando…', text: 'Aguarde alguns instantes.', action: 'none' },
    };
  }
  return {
    tone: 'waiting',
    label: 'aguardando o paciente',
    note: 'Aguardando o paciente entrar na sala…',
  };
}

export function patientDisplayName(appointment) {
  const info = (appointment && appointment.userInfo) || {};
  const name = [info.firstName, info.lastName]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(' ');
  return name || 'Paciente';
}

export function initialsOf(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return `${first}${last}`.toUpperCase();
}

// Número com "+" já traz o país; sem ele o cadastro é brasileiro.
export function whatsappUrl(userInfo) {
  const raw = (userInfo && (userInfo.whatsapp || userInfo.mobile)) || '';
  const digits = String(raw).replace(/\D/g, '');
  if (!digits) {
    return null;
  }
  const prefix = String(raw).includes('+') ? '' : '55';
  return `https://wa.me/${prefix}${digits}`;
}

// Só a consulta aberta pela agenda traz o horário; a da notificação de presença não.
export function appointmentHourLabel(appointment) {
  const start = appointment && appointment.start;
  if (!start) {
    return '';
  }
  const value = moment(start);
  if (!value.isValid()) {
    return '';
  }
  return `Consulta das ${value.format('HH:mm')}`;
}
